import React from 'react';
import { MoreHorizontalIcon, HeartIcon, MessageCircleIcon, SendIcon } from 'lucide-react';
import { SectionHeading } from './Section';
import { BrowserListing } from './BrowserListing';
import { PhoneListing } from './PhoneListing';
import { adCreatives, agent, brokerage, type AdCreative } from '../../../data/reportPhilly';
/* Brand glyphs (inline SVG). */
function FacebookGlyph({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden data-id="element-712" data-mp="21rjp31h3xn82">
      <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.9h2.54V9.85c0-2.52 1.49-3.91 3.78-3.91 1.1 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.78-1.63 1.57v1.88h2.78l-.44 2.9h-2.34V22c4.78-.79 8.43-4.94 8.43-9.94Z" data-id="element-713" data-mp="1xq7yotqesxyt" />
    </svg>;
}
function GoogleGlyph({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} aria-hidden data-id="element-714" data-mp="2gmg889qgfuzx">
      <path fill="#4285F4" d="M21.6 12.2c0-.64-.06-1.25-.16-1.84H12v3.49h5.38a4.6 4.6 0 0 1-2 3.02v2.5h3.23c1.89-1.74 2.99-4.3 2.99-7.17Z" data-id="element-715" data-mp="21sbzpcjrfj4m" />
      <path fill="#34A853" d="M12 22c2.7 0 4.96-.9 6.61-2.43l-3.23-2.5c-.9.6-2.04.96-3.38.96-2.6 0-4.8-1.76-5.59-4.12H3.08v2.59A10 10 0 0 0 12 22Z" data-id="element-716" data-mp="26qwzjav3o0sj" />
      <path fill="#FBBC05" d="M6.41 13.91a6 6 0 0 1 0-3.82V7.5H3.08a10 10 0 0 0 0 9l3.33-2.59Z" data-id="element-717" data-mp="1x0k7yyovb7cw" />
      <path fill="#EA4335" d="M12 5.96c1.47 0 2.78.5 3.81 1.49l2.85-2.85C16.95 2.98 14.69 2 12 2A10 10 0 0 0 3.08 7.5l3.33 2.59C7.2 7.72 9.4 5.96 12 5.96Z" data-id="element-718" data-mp="1j09h3vnk5srq" />
    </svg>;
}
/* GOOGLE LEADERBOARD — fully designed edge-to-edge banner. */
function Leaderboard({
  ad
}: {
  ad: AdCreative;
}) {
  return <figure className="overflow-hidden border border-border shadow-sm" data-id="element-719" data-mp="1e4u5799426xy">
      <div className="relative flex min-h-[170px] flex-col items-stretch bg-bhhs-cab text-white sm:flex-row" data-id="element-720" data-mp="34gi8tznjiefi">
        <div className="relative sm:w-[46%]" data-id="element-721" data-mp="38g8yg01byoig">
          <img src={ad.image} alt={ad.headline} className="h-40 w-full object-cover sm:h-full" data-id="element-722" data-mp="l836q7oenonh" />
          <div className="report-scrim absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-bhhs-cab/60" data-id="element-723" data-mp="g7ouedfrtsjn" />
        </div>
        <div className="relative flex flex-1 flex-col justify-center gap-2 px-6 py-5" data-id="element-724" data-mp="c9hmm83ac1kl">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60" data-id="element-725" data-mp="7amtac7oyum8">
            {ad.eyebrow}
          </span>
          <h3 className="font-display text-2xl font-semibold leading-tight" data-id="element-726" data-mp="zwpkw62aqad4">
            {ad.headline}
          </h3>
          <p className="max-w-md text-sm leading-relaxed text-white/80" data-id="element-727" data-mp="v5h9x5lbigxu">
            {ad.body}
          </p>
          <div className="mt-1 flex items-center gap-3" data-id="element-728" data-mp="q6mrw5v6cwes">
            <span className="inline-flex items-center justify-center bg-white px-4 py-2 text-sm font-semibold text-bhhs-cab" data-id="element-729" data-mp="1nmjvsi321ouq">
              {ad.cta}
            </span>
          </div>
        </div>
      </div>
      <figcaption className="flex items-center gap-2 border-t border-border bg-card px-5 py-2.5 text-xs font-medium text-muted-foreground" data-id="element-730" data-mp="1q7rjdimr3a4m">
        <GoogleGlyph className="size-4" data-id="element-731" data-mp="1v6up75ak1kdn" /> Google · {ad.placement}
      </figcaption>
    </figure>;
}
/* GOOGLE MEDIUM RECTANGLE */
function RectangleAd({
  ad
}: {
  ad: AdCreative;
}) {
  return <figure className="flex h-full flex-col overflow-hidden border border-border bg-card shadow-sm" data-id="element-732" data-mp="248khseg6apd0">
      <div className="relative" data-id="element-733" data-mp="295kn2n577o32">
        <img src={ad.image} alt={ad.headline} className="aspect-[4/3] w-full object-cover" data-id="element-734" data-mp="2949hqiw3u89u" />
        <span className="absolute left-3 top-3 bg-bhhs-cab/85 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white" data-id="element-735" data-mp="2ivneiiwo8tza">
          {ad.eyebrow}
        </span>
      </div>
      <div className="flex flex-1 flex-col gap-2 p-5" data-id="element-736" data-mp="2rwvdl370hyuf">
        <h3 className="font-display text-xl font-semibold leading-tight text-bhhs-cab" data-id="element-737" data-mp="2ryeu8uwkybwq">
          {ad.headline}
        </h3>
        <p className="text-sm text-muted-foreground" data-id="element-738" data-mp="2wtc3o2xruna5">{ad.body}</p>
        <span className="mt-auto inline-flex w-fit items-center justify-center bg-bhhs-cab px-4 py-2 text-sm font-medium text-white" data-id="element-739" data-mp="2j7ennmsu057r">
          {ad.cta}
        </span>
      </div>
      <figcaption className="flex items-center gap-2 border-t border-border px-5 py-2.5 text-xs font-medium text-muted-foreground" data-id="element-740" data-mp="2sxprl9cimeen">
        <GoogleGlyph className="size-4" data-id="element-741" data-mp="2o1r8o19yholm" /> Google · {ad.placement}
      </figcaption>
    </figure>;
}
/* META / FACEBOOK FEED */
function MetaAd({
  ad
}: {
  ad: AdCreative;
}) {
  return <figure className="flex h-full flex-col overflow-hidden border border-border bg-card shadow-sm" data-id="element-742" data-mp="1wa2ztoqdzk7j">
      <div className="flex items-center gap-2.5 p-3" data-id="element-743" data-mp="2a9ksif6szhm6">
        <img src={agent.avatar} alt="" className="size-9 rounded-full object-cover" data-id="element-744" data-mp="258nb44tmarlq" />
        <div className="leading-tight" data-id="element-745" data-mp="3lm549p86ckmt">
          <p className="text-[13px] font-semibold" data-id="element-746" data-mp="3lp9fh6k24gmz">{brokerage.name}</p>
          <p className="text-[11px] text-muted-foreground" data-id="element-747" data-mp="3gntsva28e22h">
            Sponsored ·{' '}
            <FacebookGlyph className="inline size-3 text-[#1877F2]" data-id="element-748" data-mp="12ft0d4kciual" />
          </p>
        </div>
        <MoreHorizontalIcon className="ml-auto size-4 text-muted-foreground" data-id="element-749" data-mp="17ggo9hda2v0l" />
      </div>
      <p className="px-3 pb-2.5 text-sm" data-id="element-750" data-mp="tgxq9he6l020">
        {ad.headline} — {ad.body}
      </p>
      <img src={ad.image} alt={ad.headline} className="aspect-square w-full object-cover" data-id="element-751" data-mp="ye7dqri0f1n7" />
      <div className="flex items-center justify-between bg-muted/40 px-3 py-2" data-id="element-752" data-mp="1m5uw2l17h4dv">
        <span className="text-xs uppercase tracking-wide text-muted-foreground" data-id="element-753" data-mp="1q6eurt1fgg24">
          foxroach.com
        </span>
        <span className="bg-bhhs-cab px-3 py-1.5 text-xs font-semibold text-white" data-id="element-754" data-mp="1cdyzjeerpmbr">
          {ad.cta}
        </span>
      </div>
      <div className="flex items-center gap-6 border-t border-border px-4 py-2 text-muted-foreground" data-id="element-755" data-mp="1hbqoq0o12uc9">
        <HeartIcon className="size-4" data-id="element-756" data-mp="251l0sg0iwevg" />
        <MessageCircleIcon className="size-4" data-id="element-757" data-mp="29ue552s2xl70" />
        <SendIcon className="size-4" data-id="element-758" data-mp="29w8s0mmwvndq" />
      </div>
      <figcaption className="flex items-center gap-2 border-t border-border px-4 py-2.5 text-xs font-medium text-muted-foreground" data-id="element-759" data-mp="2jqebzlyx0vco">
        <FacebookGlyph className="size-4 text-[#1877F2]" data-id="element-760" data-mp="2erkwxp3zcwae" /> Meta ·{' '}
        {ad.placement}
      </figcaption>
    </figure>;
}
// Marketing assets: the listing live across devices, plus the real ad creatives
// (Google Display leaderboard + rectangle, Meta feed).
export function MarketingAssets() {
  const leaderboard = adCreatives.find(a => a.shape === 'leaderboard')!;
  const rectangle = adCreatives.find(a => a.shape === 'rectangle')!;
  const meta = adCreatives.find(a => a.shape === 'square')!;
  return <section className="print:break-before-page" data-id="element-761" data-mp="1qzcuvfbnwpqi">
      <SectionHeading eyebrow="The creative" title="Marketing assets" description="Your home presented across every screen, paired with confident, luxury-grade ad creative built to make qualified buyers reach out." data-id="element-762" data-mp="1m35ndi3mb66g" />

      {/* Live on desktop + mobile — clean recreations of the Fox & Roach
          listing page in a minimal browser frame and a phone. */}
      <div className="relative mb-5 overflow-hidden border border-border bg-bhhs-cab px-5 py-10 sm:px-10 sm:py-12" data-id="element-763" data-mp="3sq3fxi5gkmia">
        <div className="pointer-events-none absolute inset-0 opacity-[0.06] [background-image:linear-gradient(white_1px,transparent_1px),linear-gradient(90deg,white_1px,transparent_1px)] [background-size:40px_40px]" data-id="element-764" data-mp="1ld8n9exv0nr" />
        <div className="relative mx-auto flex max-w-3xl items-end justify-center gap-4 sm:-mr-2 lg:gap-0" data-id="element-765" data-mp="352n1njf12sac">
          <div className="min-w-0 flex-1" data-id="element-766" data-mp="3a0eqg4kdlyab">
            <BrowserListing data-id="element-767" data-mp="3eyq1gimeqeu4" />
          </div>
          <div className="hidden sm:-mb-6 sm:block lg:-ml-12" data-id="element-768" data-mp="3exne2koamuiy">
            <PhoneListing data-id="element-769" data-mp="3jq8378w98jg5" />
          </div>
        </div>
      </div>

      {/* Ad creatives */}
      <div className="ad-print-cap space-y-5 print:space-y-3" data-id="element-770" data-mp="2mb2mbj3wogev">
        <Leaderboard ad={leaderboard} data-id="element-771" data-mp="2m8q5585z2a2v" />
        <div className="grid grid-cols-1 items-start gap-5 sm:grid-cols-2 print:break-before-page" data-id="element-772" data-mp="2r8vqy7pk92d1">
          <RectangleAd ad={rectangle} data-id="element-773" data-mp="1kljmk3dc9xlr" />
          <MetaAd ad={meta} data-id="element-774" data-mp="1kml851rze2hq" />
        </div>
      </div>
    </section>;
}